type ColumnGroup<T> = {
  groupId: string;
  label: string;
  items: T[];
};

type AnyRecord = Record<string, unknown>;

/**
 * Groups items by a specific column value.
 *
 * @example
 * const users = [
 *   { name: "Alice", role: "admin", department: "Engineering" },
 *   { name: "Bob", role: "user", department: "Engineering" },
 *   { name: "Carol", role: "admin", department: "Marketing" },
 *   { name: "Dave", role: "user", department: "Marketing" }
 * ];
 *
 * const result = groupByColumn(users, "role");
 * // Returns:
 * // [
 * //   { label: "admin", items: [{ name: "Alice", role: "admin", ... }, { name: "Carol", role: "admin", ... }] },
 * //   { label: "user", items: [{ name: "Bob", role: "user", ... }, { name: "Dave", role: "user", ... }] }
 * // ]
 */
export function groupByColumn<T extends AnyRecord>(
  items: T[],
  columnKey: keyof T,
  sortItems?: (a: T, b: T) => number,
): ColumnGroup<T>[] {
  const groups: Record<string, T[]> = {};

  items.forEach((item) => {
    const columnValue = item[columnKey];

    // Convert the column value to a string for grouping
    let groupKey: string;
    if (columnValue === null || columnValue === undefined) {
      groupKey = "Unknown";
    } else {
      groupKey = String(columnValue);
    }

    if (!(groupKey in groups)) {
      groups[groupKey] = [];
    }

    groups[groupKey].push(item);
  });

  if (sortItems) {
    Object.keys(groups).forEach((key) => {
      groups[key] = groups[key].sort(sortItems);
    });
  }

  return Object.entries(groups)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([label, groupItems]) => ({
      groupId: label,
      label,
      items: groupItems,
    }));
}

/**
 * Generic sort functions that can work with any field
 */
export const sortFunctions = {
  /**
   * Sort alphabetically by any string field (ascending)
   * @param field - The field to sort by
   */
  alphabetical:
    <T extends AnyRecord>(field: keyof T) =>
    (a: T, b: T) => {
      const valueA = String(a[field] || "");
      const valueB = String(b[field] || "");
      return valueA.localeCompare(valueB);
    },

  /**
   * Sort chronologically by any date field (newest first)
   * @param field - The date field to sort by
   */
  chronological:
    <T extends AnyRecord>(field: keyof T) =>
    (a: T, b: T) => {
      const dateA = new Date(String(a[field] || 0)).getTime();
      const dateB = new Date(String(b[field] || 0)).getTime();
      return dateB - dateA;
    },
};
